import { Injectable } from '@angular/core';

export interface AmortizationRow {
  paymentNumber: number;
  paymentDate: Date;
  payment: number;
  principal: number;
  interest: number;
  remainingBalance: number;
  paid?: boolean; // Optional flag to mark payments as paid
  state?: string; // Optional state of the payment (Funding, Scheduled, Paid, etc.)
}

@Injectable({
  providedIn: 'root'
})
export class LoanCalculatorService {
  /**
   * Calculate payment given principal, interest rate, term, and frequency
   * @param principal Loan amount
   * @param annualInterestRate Annual interest rate (as percentage, e.g. 5.5 for 5.5%)
   * @param termMonths Loan term in months
   * @param frequency Payment frequency ('annually','semi-annually','quarterly','monthly','bi-weekly','weekly')
   * @returns Payment amount for the given frequency
   */
  calculatePayment(principal: number, annualInterestRate: number, termMonths: number, frequency: string = 'monthly'): number {
    const paymentsPerYear = this.getPaymentsPerYear(frequency);
    const ratePerPeriod = (annualInterestRate / 100) / paymentsPerYear;
    const totalPayments = (termMonths / 12) * paymentsPerYear;

    return (principal * ratePerPeriod * Math.pow(1 + ratePerPeriod, totalPayments)) / 
           (Math.pow(1 + ratePerPeriod, totalPayments) - 1);
  }

  /**
   * Calculate loan amount given payment, interest rate, term, and frequency
   * @param payment Payment amount for the given frequency
   * @param annualInterestRate Annual interest rate (as percentage)
   * @param termMonths Loan term in months
   * @param frequency Payment frequency ('annually','semi-annually','quarterly','monthly','bi-weekly','weekly')
   * @returns Principal loan amount
   */
  calculatePrincipal(payment: number, annualInterestRate: number, termMonths: number, frequency: string = 'monthly'): number {
    const paymentsPerYear = this.getPaymentsPerYear(frequency);
    const ratePerPeriod = (annualInterestRate / 100) / paymentsPerYear;
    const totalPayments = (termMonths / 12) * paymentsPerYear;

    return (payment * (Math.pow(1 + ratePerPeriod, totalPayments) - 1)) / 
           (ratePerPeriod * Math.pow(1 + ratePerPeriod, totalPayments));
  }

  /**
   * Calculate interest rate given principal, payment, term, and frequency
   * Uses binary search method for approximation
   * @param principal Loan amount
   * @param payment Payment amount for the given frequency
   * @param termMonths Loan term in months
   * @param frequency Payment frequency ('annually','semi-annually','quarterly','monthly','bi-weekly','weekly')
   * @returns Annual interest rate as percentage
   */
  calculateInterestRate(principal: number, payment: number, termMonths: number, frequency: string = 'monthly'): number {
    
    // Binary search approach
    let low = 0;      // 0%
    let high = 100;   // 100%
    const TOLERANCE = 0.01;
    const MAX_ITERATIONS = 50;

    const paymentsPerYear = this.getPaymentsPerYear(frequency);
    const totalPayments = (termMonths / 12) * paymentsPerYear;

    for (let i = 0; i < MAX_ITERATIONS; i++) {
      const guess = (low + high) / 2;
      const ratePerPeriod = guess / (100 * paymentsPerYear);
      const factor = Math.pow(1 + ratePerPeriod, totalPayments);
      
      const calculated = (principal * ratePerPeriod * factor) / (factor - 1);
      
      if (Math.abs(calculated - payment) < TOLERANCE) {
        return guess;
      }

      if (calculated > payment) {
        high = guess;
      } else {
        low = guess;
      }

      // If the range is very small, we've converged
      if (high - low < TOLERANCE) {
        const finalGuess = (low + high) / 2;
        return finalGuess;
      }
    }

    throw new Error('Interest rate calculation did not converge');
  }

  /**
   * Calculate loan term given principal, payment, interest rate, and frequency
   * @param principal Loan amount
   * @param payment Payment amount for the given frequency
   * @param annualInterestRate Annual interest rate (as percentage)
   * @param frequency Payment frequency ('annually','semi-annually','quarterly','monthly','bi-weekly','weekly')
   * @returns Loan term in months
   */
  calculateTerm(principal: number, payment: number, annualInterestRate: number, frequency: string = 'monthly'): number {
    const paymentsPerYear = this.getPaymentsPerYear(frequency);
    const ratePerPeriod = (annualInterestRate / 100) / paymentsPerYear;
    const termInPeriods = Math.log(payment / (payment - principal * ratePerPeriod)) / Math.log(1 + ratePerPeriod);
    return (termInPeriods * 12) / paymentsPerYear;
  }

  /**
   * Get number of payments per year based on frequency
   * @param frequency Payment frequency
   * @returns Number of payments per year
   */
  private getPaymentsPerYear(frequency: string): number {
    switch (frequency) {
      case 'annually': return 1;
      case 'semi-annually': return 2;
      case 'quarterly': return 4;
      case 'monthly': return 12;
      case 'bi-weekly': return 26;
      case 'weekly': return 52;
      default: return 12; // Default to monthly
    }
  }

  /**
   * Calculate Volume Rate of Return (VRR)
   * VRR = (Total Payments - Principal) / (Average Outstanding Principal * Years)
   */
  calculateVRR(principal: number, payment: number, termMonths: number, firstPaymentDate: Date, closeDate: Date, frequency: string = 'monthly'): number {
    const paymentsPerYear = this.getPaymentsPerYear(frequency);
    const totalPayments = payment * Math.ceil((termMonths / 12) * paymentsPerYear);
    const years = termMonths / 12;
    
    // Calculate average outstanding principal using amortization schedule
    const schedule = this.generateAmortizationSchedule(principal, payment, termMonths, firstPaymentDate, closeDate, frequency);
    const avgOutstandingPrincipal = schedule.reduce((sum, row) => sum + row.remainingBalance, 0) / schedule.length;
    
    return ((totalPayments - principal) / (avgOutstandingPrincipal * years)) * 100;
  }

  /**
   * Generate complete amortization schedule
   */
  generateAmortizationSchedule(principal: number, payment: number, termMonths: number, firstPaymentDate: Date, closeDate: Date, frequency: string = 'monthly'): AmortizationRow[] {
    const schedule: AmortizationRow[] = [];
    let balance = principal;
    
    // Calculate interest rate based on frequency
    const annualRate = this.calculateInterestRate(principal, payment, termMonths, frequency);
    const paymentsPerYear = this.getPaymentsPerYear(frequency);
    const ratePerPeriod = annualRate / (100 * paymentsPerYear); // Convert annual rate to per-period decimal

    // Calculate days in first period
    const msPerDay = 1000 * 60 * 60 * 24;
    const firstPeriodDays = Math.round((firstPaymentDate.getTime() - closeDate.getTime()) / msPerDay);
    const daysInYear = 365;
    
    // Calculate first payment (partial period)
    const firstPeriodRate = (annualRate / 100) * (firstPeriodDays / daysInYear);
    const firstPeriodInterest = balance * firstPeriodRate;
    const firstPeriodPrincipal = payment - firstPeriodInterest;
    balance = Math.max(0, balance - firstPeriodPrincipal);

    // Add first payment
    schedule.push({
      paymentNumber: 1,
      paymentDate: new Date(firstPaymentDate),
      payment: payment,
      principal: firstPeriodPrincipal,
      interest: firstPeriodInterest,
      remainingBalance: balance
    });

    // Calculate remaining payments
    let currentDate = new Date(firstPaymentDate);
    const totalPayments = Math.ceil((termMonths / 12) * paymentsPerYear);
    
    for (let i = 2; i <= totalPayments && balance > 0; i++) {
      currentDate = new Date(currentDate);
      // Add period to date based on frequency
      switch (frequency) {
        case 'weekly':
          currentDate.setDate(currentDate.getDate() + 7);
          break;
        case 'bi-weekly':
          currentDate.setDate(currentDate.getDate() + 14);
          break;
        case 'monthly':
          currentDate.setMonth(currentDate.getMonth() + 1);
          break;
        case 'quarterly':
          currentDate.setMonth(currentDate.getMonth() + 3);
          break;
        case 'semi-annually':
          currentDate.setMonth(currentDate.getMonth() + 6);
          break;
        case 'annually':
          currentDate.setFullYear(currentDate.getFullYear() + 1);
          break;
      }

      const interest = balance * ratePerPeriod;
      const principalPaid = payment - interest;
      balance = Math.max(0, balance - principalPaid);

      schedule.push({
        paymentNumber: i,
        paymentDate: new Date(currentDate),
        payment: payment,
        principal: principalPaid,
        interest: interest,
        remainingBalance: balance
      });
    }

    return schedule;
  }
}
